/**
 * TanStack Query hooks for the project-anchored chat-session REST surface
 * landed in Phase A (OR-5963):
 *
 *   POST   /v1/message-gateway/projects/:projectId/chats     → create
 *   GET    /v1/message-gateway/projects/:projectId/chats     → list
 *   PATCH  /v1/message-gateway/chats/:chatId                 → update
 *   DELETE /v1/message-gateway/chats/:chatId                 → soft delete
 *
 * Every endpoint is gated on ``research_assistant_gen_3`` on the BE
 * (404 when the flag is OFF). The hooks here don't re-check the flag —
 * the route boundary already guards against mounting the surface when
 * the flag is OFF.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { createChatMutationKey } from '@verantos-dev/feature-epi-chat';
import type { ObjectResponse } from '@verantos-dev/types-common';

import { authFetch } from './authFetch';
import { projectPageKeys } from './queryKeys';
import type {
  ChatOwnerFilter,
  ChatSession,
  CreateChatSessionBody,
  InternalChatSnapshot,
  UpdateChatSessionBody,
} from './types';

interface ProjectChatsQueryArgs {
  projectId: string;
  ownerFilter: ChatOwnerFilter;
  /**
   * Additional gate ANDed with `Boolean(projectId)`. OR-6455's `owner=all`
   * partition should only ever be fetched for an internal+flagged caller —
   * every other caller gets a 403 the BE also logs at WARNING, so callers
   * that don't need the 'all' partition pass `enabled: false` rather than
   * eating that noise. Defaults to `true` (existing 'me'/'team' behavior
   * unchanged).
   */
  enabled?: boolean;
}

/**
 * Wire envelope returned by ``GET /projects/:pid/chats`` (OR-6123 C-be).
 *
 * Was a bare ``ChatSession[]``; now an object carrying the list plus two
 * resolution signals the FE needs for AC3/AC4:
 *  - ``lastActiveChatId`` — the server's authoritative "reopen here" id
 *    (D4); ``null`` until the user has interacted with any chat.
 *  - ``everHadChat`` — whether the project has EVER had a chat, including
 *    soft-deleted ones (drives the welcome variant; see ``useWelcomeVariant``).
 *
 * Both are resolved only for ``owner=me`` on the BE; the ``team`` partition
 * carries ``null``/``false`` placeholders.
 */
interface ProjectChatsWire {
  chats: ChatSession[];
  lastActiveChatId: string | null;
  everHadChat: boolean;
}

/**
 * Normalized cache shape. Keeps ``data: ChatSession[]`` so every existing
 * consumer (``ProjectChatsList``, ``ActiveChatPicker``, ``useActiveChat``,
 * the create/update/delete mutations' direct cache reads/writes) keeps
 * working unchanged — they read ``query.data.data`` and spread ``...cur``,
 * which now preserves the two sibling signals for free. The new fields ride
 * alongside on ``query.data.lastActiveChatId`` / ``.everHadChat``.
 */
export interface ProjectChatsCache {
  data: ChatSession[];
  lastActiveChatId: string | null;
  everHadChat: boolean;
}

/**
 * Fetches and normalizes one owner-partition of a project's chat list.
 * Exported (alongside ``projectPageKeys``) so callers outside this hook can
 * warm the cache ahead of navigation — see ``useProjectChatsQuery`` below and
 * the project-list row prefetch in ``feature-projects-list`` (mirrors the
 * existing cohort-list prefetch there), which race the fetch against the
 * project-page mount so the welcome greeting isn't stuck behind a cold GET.
 */
export const fetchProjectChats = async ({
  projectId,
  ownerFilter,
}: ProjectChatsQueryArgs): Promise<ProjectChatsCache> => {
  const res = await authFetch<ObjectResponse<ProjectChatsWire>>(
    `/projects/${projectId}/chats?owner=${ownerFilter}`
  );
  return {
    data: res.data.chats,
    lastActiveChatId: res.data.lastActiveChatId,
    everHadChat: res.data.everHadChat,
  };
};

/**
 * List chat sessions for a project. ``ownerFilter`` partitions the cache
 * so flipping "Your chats" ↔ "Team chats" doesn't refetch the same list
 * — and so an optimistic-add into the "me" cache doesn't accidentally
 * leak into the "team" list.
 *
 * The wire envelope ``{ chats, lastActiveChatId, everHadChat }`` is
 * unwrapped in ``fetchProjectChats`` (not via ``select``) so the CACHED
 * value is the normalized ``{ data, lastActiveChatId, everHadChat }`` shape
 * — the mutations read/write that cache directly, so the cached and
 * component-visible shapes must be identical.
 */
export const useProjectChatsQuery = ({
  projectId,
  ownerFilter,
  enabled = true,
}: ProjectChatsQueryArgs) => {
  return useQuery<ProjectChatsCache>({
    queryKey: projectPageKeys.chatsForProject(projectId, ownerFilter),
    queryFn: () => fetchProjectChats({ projectId, ownerFilter }),
    enabled: Boolean(projectId) && enabled,
  });
};

interface CreateChatMutationArgs {
  projectId: string;
}

/**
 * Mint a new chat under a project. Optimistically prepends a placeholder
 * row into the "me" cache so the new chat appears in "Your chats"
 * immediately; the settled row replaces it once the response lands.
 *
 * The placeholder uses a synthetic ``optimistic-…`` id so the BE-stamped
 * row (real UUID) will safely append rather than collide. ``onError``
 * rolls back to the previous snapshot.
 */
export const useCreateChatMutation = ({ projectId }: CreateChatMutationArgs) => {
  const queryClient = useQueryClient();
  return useMutation({
    // Tagged so `useEpiChatPage`'s subscribe effect (feature-epi-chat) can
    // detect a still-in-flight create for a given chat id via
    // `usePendingChatCreation` and hold off subscribing until it settles —
    // closes the optimistic-create race described on `mintChat` above.
    mutationKey: createChatMutationKey(projectId),
    mutationFn: (body: CreateChatSessionBody) =>
      authFetch<ObjectResponse<ChatSession>>(`/projects/${projectId}/chats`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onMutate: async (body) => {
      const cacheKey = projectPageKeys.chatsForProject(projectId, 'me');
      // Cancel in-flight refetches so the optimistic write doesn't get
      // clobbered by a stale GET landing after the POST went out.
      await queryClient.cancelQueries({ queryKey: cacheKey });

      const previous = queryClient.getQueryData<ProjectChatsCache>(cacheKey);

      // Build a placeholder row that satisfies enough of the list-row
      // render to look "real" until the real id arrives. The list
      // sorts/filters by `lastActiveAt`, so we stamp `Date.now()` to keep
      // it at the top.
      //
      // When the caller supplied a client-minted id (the optimistic-
      // navigation path — see ``ChatPanelEmptyState`` / ``handleNewChat``),
      // use it for the placeholder so the URL's ``?chat=<id>`` resolves
      // to a real row immediately via ``useActiveChat`` instead of
      // flashing through a "still loading" state until POST settles.
      // The BE-stamped row arrives on the same id, so the optimistic
      // row gets seamlessly replaced rather than duplicated.
      const now = new Date().toISOString();
      const placeholder: ChatSession = {
        id: body.id ?? `optimistic-${now}`,
        projectId,
        // Left empty on the optimistic placeholder. A real ownerUserId only
        // appears once the server-confirmed row is in cache, so consumers use
        // a non-empty ownerUserId to tell a committed chat from a placeholder.
        ownerUserId: '',
        ownerDisplayName: null,
        title: body.title ?? null,
        createdAt: now,
        lastActiveAt: now,
        deletedAt: null,
        shareState: 'private',
        lockedAt: null,
        tokenCount: 0,
        inflightTaskScope: null,
      };

      queryClient.setQueryData<ProjectChatsCache>(cacheKey, (cur) => ({
        ...(cur ?? { data: [], lastActiveChatId: null, everHadChat: false }),
        data: [placeholder, ...(cur?.data ?? [])],
      }));

      return { previous, cacheKey } as const;
    },
    onSuccess: (response) => {
      // The POST response is the server-confirmed row. Write it into the "me"
      // cache so consumers see a committed chat (real ownerUserId) at once,
      // rather than waiting on a list refetch — list reads are eventually
      // consistent and can briefly omit a just-created row.
      const cacheKey = projectPageKeys.chatsForProject(projectId, 'me');
      const created = response.data;
      queryClient.setQueryData<ProjectChatsCache>(cacheKey, (cur) => {
        const rows = (cur?.data ?? []).filter((c) => c.id !== created.id);
        return {
          ...(cur ?? { data: [], lastActiveChatId: null, everHadChat: false }),
          data: [created, ...rows],
        };
      });
    },
    onError: (err, _body, context) => {
      if (!context) return;
      // 409 means the row already exists on the BE — typical cause is
      // a benign double-POST from the optimistic-create path. Don't
      // roll back: the optimistic placeholder is correct, and the
      // ``onSettled`` invalidation will refresh it with the real row.
      const status = (err as { status?: number } | null)?.status;
      if (status === 409) return;
      queryClient.setQueryData(context.cacheKey, context.previous);
    },
    onSettled: (_data, error) => {
      // Success already wrote the authoritative row in onSuccess; refetching
      // would replace it with an eventually-consistent list that can omit a
      // just-created row. Only converge via the list on error: a 409
      // (duplicate create) means the row exists but the response carried no
      // body to cache. The "team" list is untouched — a new chat is private.
      if (error) {
        void queryClient.invalidateQueries({
          queryKey: projectPageKeys.chatsForProject(projectId, 'me'),
        });
      }
    },
  });
};

interface UpdateChatMutationVars {
  chatId: string;
  body: UpdateChatSessionBody;
}

/**
 * Patch title / shareState on a chat. Invalidates both list partitions
 * because a share-state flip moves a chat between "Your chats" (private,
 * owner=me) and "Team chats" (project_readonly, owner!=me) for other
 * teammates' views — when this user owns it, it stays in "me" either
 * way, but a refetch is the conservative call.
 *
 * The mutation isn't bound to a specific projectId because the route
 * is project-less (`/chats/:chatId`). Callers know which project the
 * chat belongs to and invalidate accordingly; the default below is to
 * invalidate every cached project-chats list.
 */
export const useUpdateChatMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ chatId, body }: UpdateChatMutationVars) =>
      authFetch<ObjectResponse<ChatSession>>(`/chats/${chatId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectPageKeys.chats() });
    },
  });
};

interface DeleteChatMutationVars {
  chatId: string;
  /**
   * Client-side only — never sent on the wire (the DELETE route is
   * project-less, `/chats/:chatId`). Needed to target the right
   * `chatsForProject(projectId, 'me')` cache entry for the optimistic
   * removal below; the caller always has it on the `ChatSession` row.
   */
  projectId: string;
}

/**
 * Soft-delete a chat. Optimistically removes the row (and clears
 * `lastActiveChatId` if it pointed at the deleted chat) from the "me"
 * cache — delete is only ever called on rows the user owns (see the
 * component's owner-gating note).
 *
 * This isn't just a nicety: without it, deleting a project's ONLY chat
 * left the UI showing the deleted chat until a hard refresh. The
 * plain invalidate-and-refetch below fires the row-deleted callback
 * (which clears the sticky/URL chat id) before the refetch lands;
 * `useEffectiveChatId`'s fallback resolution then reads the still-stale
 * cache — whose `lastActiveChatId` still points at the just-deleted
 * chat — and navigates right back to it. With 2+ chats this race is
 * invisible (the stale cache still has a different, live chat to fall
 * back to); with exactly one, the bogus resolution is indistinguishable
 * from "nothing was deleted."
 */
export const useDeleteChatMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ chatId }: DeleteChatMutationVars) =>
      authFetch<void>(`/chats/${chatId}`, {
        method: 'DELETE',
      }),
    onMutate: async ({ chatId, projectId }) => {
      const cacheKey = projectPageKeys.chatsForProject(projectId, 'me');
      await queryClient.cancelQueries({ queryKey: cacheKey });
      const previous = queryClient.getQueryData<ProjectChatsCache>(cacheKey);
      queryClient.setQueryData<ProjectChatsCache>(cacheKey, (cur) => {
        if (!cur) return cur;
        return {
          ...cur,
          data: cur.data.filter((c) => c.id !== chatId),
          lastActiveChatId: cur.lastActiveChatId === chatId ? null : cur.lastActiveChatId,
        };
      });
      return { previous, cacheKey } as const;
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      queryClient.setQueryData(context.cacheKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: projectPageKeys.chats() });
    },
  });
};

/**
 * Look up a single chat by id for use inside the active chat panel —
 * we need the title and share state to render header controls.
 *
 * Derives from the two list queries (``me`` + ``team``) rather than a
 * dedicated single-chat endpoint: there's no ``GET /chats/:chatId``
 * on the BE today, and the list payloads carry every field this caller
 * needs. The hook subscribes to both partitions so a chat moving between
 * ``share_state`` states keeps the lookup current.
 *
 * Returns ``isOwn=true`` when the chat is found in the ``me`` cache —
 * the BE list endpoint already filters by ``owner_user_id``, so the
 * partition itself is the ownership signal. ``team``-cache hits are
 * read-only viewers.
 *
 * ``viaInternalVisibility=true`` (OR-6455) means the chat is neither the
 * caller's own nor a project-readonly-shared team chat — it was only
 * found via the ``owner=all`` internal-viewer partition. This is the
 * signal ``ProjectChatPanel`` uses to render the read-only snapshot view
 * instead of the live subscribed chat: a bug here that misclassified a
 * chat as owned would be a real safety issue (it would render the live,
 * writable panel), so this only ever flips true when neither ``me`` nor
 * ``team`` matched first.
 *
 * ``internalVisibilityEnabled`` is a precondition, not the sole gate, for
 * whether the ``all`` partition is fetched — pass ``false`` (the default)
 * for every caller except an internal user with the OR-6455 flag on. Even
 * when true, the ``all`` query only actually fires once ``me``/``team``
 * have settled AND confirmed ``chatId`` isn't already found in either —
 * see ``needsAllPartition`` below. Firing it unconditionally on every
 * chat render (this hook mounts twice per chat open — once in
 * ``ProjectChatPanel``, once in the sibling ``MaybeActiveChatProvider``)
 * turned the ``owner=all`` audit WARNING into noise on ordinary chat opens,
 * own chats included, and issued a needless Auth0 lookup each time.
 *
 * TODO: replace with a dedicated ``GET /chats/:chatId`` query once the
 * BE endpoint lands. cleanup with research_assistant_gen_3 ff.
 */
export const useActiveChat = ({
  projectId,
  chatId,
  internalVisibilityEnabled = false,
}: {
  projectId: string;
  chatId: string | undefined;
  internalVisibilityEnabled?: boolean;
}): {
  chat: ChatSession | null;
  isOwn: boolean;
  isLoading: boolean;
  viaInternalVisibility: boolean;
} => {
  const meQuery = useProjectChatsQuery({ projectId, ownerFilter: 'me' });
  const teamQuery = useProjectChatsQuery({ projectId, ownerFilter: 'team' });

  // Computed ahead of the ``all`` query so it can gate that query's
  // ``enabled`` — see the docstring above. ``chatId ?  : undefined`` guards
  // the pre-``if (!chatId)`` window below, where hooks must still be called
  // unconditionally.
  const fromMe = chatId ? meQuery.data?.data.find((c) => c.id === chatId) : undefined;
  const fromTeam = chatId ? teamQuery.data?.data.find((c) => c.id === chatId) : undefined;
  const needsAllPartition =
    internalVisibilityEnabled &&
    Boolean(chatId) &&
    meQuery.isSuccess &&
    teamQuery.isSuccess &&
    !fromMe &&
    !fromTeam;
  const allQuery = useProjectChatsQuery({
    projectId,
    ownerFilter: 'all',
    enabled: needsAllPartition,
  });

  if (!chatId) {
    return { chat: null, isOwn: false, isLoading: false, viaInternalVisibility: false };
  }

  if (fromMe) {
    return { chat: fromMe, isOwn: true, isLoading: false, viaInternalVisibility: false };
  }

  if (fromTeam) {
    return { chat: fromTeam, isOwn: false, isLoading: false, viaInternalVisibility: false };
  }

  if (internalVisibilityEnabled) {
    const fromAll = allQuery.data?.data.find((c) => c.id === chatId);
    if (fromAll) {
      return { chat: fromAll, isOwn: false, isLoading: false, viaInternalVisibility: true };
    }
  }

  // Still loading the relevant partitions — treat as loading until they
  // settle so we don't flash an "unknown chat" state.
  const isLoading =
    meQuery.isLoading || teamQuery.isLoading || (needsAllPartition && allQuery.isLoading);
  return { chat: null, isOwn: false, isLoading, viaInternalVisibility: false };
};

/**
 * OR-6455: fetch a one-shot, read-only snapshot of a chat the caller
 * doesn't own — via `GET /chats/:chatId/internal-snapshot`, gated
 * server-side on `is_authorized_for_internal_chat_visibility`. Never
 * refetches automatically (no polling, no live subscription — that's the
 * whole point); the caller's `refetch()` is the only way this updates,
 * surfaced as an explicit "Refresh" action rather than implying a live
 * view.
 */
export const useInternalChatSnapshotQuery = ({
  chatId,
  enabled,
}: {
  chatId: string;
  enabled: boolean;
}) => {
  return useQuery<InternalChatSnapshot>({
    queryKey: projectPageKeys.internalChatSnapshot(chatId),
    queryFn: () =>
      authFetch<ObjectResponse<InternalChatSnapshot>>(`/chats/${chatId}/internal-snapshot`).then(
        (res) => res.data
      ),
    enabled,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};
